import React, {FC, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {IconButton, Card, CardContent, Typography} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {Modal, ModalHeader, ModalBody} from 'reactstrap';
import Moment from 'react-moment';
import {LoadingIndicator} from '../../LoadingIndicator';
import MachineStatus from '../../../pages/Machines/machine-status';
import {MachineForm} from '../MachineForm';
import {createLoadingSelector} from '../../../selectors/loading';
import {EDIT_MACHINE} from '../../../store/machine/actionTypes';
import {getLoggedInUser} from '../../../helpers/fakebackend_helper';
import {ROLE, RoleNameMap} from '../../Role/constants';

interface MachineCardProps {
  machine?: Machine;
  isLoading?: boolean;
}

export const MachineCard: FC<MachineCardProps> = ({machine, isLoading}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIsLoading, setModalIsLoading] = useState(false);

  const authUser = getLoggedInUser();
  const userRole = RoleNameMap.get(authUser.user.role);

  const loadingSelector = useSelector(createLoadingSelector([EDIT_MACHINE]));

  useEffect(() => {
    setModalIsLoading(loadingSelector);
  }, [loadingSelector]);

  const canEdit =
    [ROLE.BROTHER_MANAGER, ROLE.BROTHER_STAFF, ROLE.DEALER_ENGINEER].includes(userRole!);

  return (
    <Card>
      <CardContent>
        <div className="d-flex align-items-start mb-3">
          <div className="avatar-sm mr-3">
            <span className="avatar-title rounded-circle bg-primary text-white font-size-20">
              <i className="bx bx-dumbbell" />
            </span>
          </div>

          <div className="flex-grow-1">
            {!isLoading && machine ? (
              <>
                <div className="mb-2">
                  <MachineStatus machine={machine} />
                </div>
                <div className="d-flex align-items-center mb-2">
                  <i className="bx bx-power-off bx-xs mr-2" />
                  <Typography variant="body2" className="text-truncate">
                    {machine.installation_date ? (
                      <Moment format="DD-MM-YYYY">{machine.installation_date}</Moment>
                    ) : (
                      'Not installed'
                    )}
                  </Typography>
                </div>
              </>
            ) : (
              <LoadingIndicator />
            )}
          </div>

          {canEdit && (
            <div className="ml-auto">
              <IconButton aria-label="edit" onClick={() => setModalOpen(true)} disabled={isLoading}>
                <EditIcon />
              </IconButton>

              <Modal
                size="lg"
                isOpen={modalOpen}
                centered
                toggle={() => setModalOpen(!modalOpen)}
                className="machineEditModal"
              >
                <div className="modal-content">
                  <ModalHeader toggle={() => setModalOpen(!modalOpen)}>
                    Edit Machine {modalIsLoading && <LoadingIndicator />}
                  </ModalHeader>
                  <ModalBody>{machine && <MachineForm machine={machine} />}</ModalBody>
                </div>
              </Modal>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
